import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useOrganization } from "../../../../context/OrganizationContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "../../../../hooks/useDebounce";
import { encrypt, handleFilterChange } from "../../../../helpers";
import Table from "../../../../components/elements/table/Table";
import Search from "../../../../components/form/Search";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { toast } from "react-toastify";
import { transactionList } from "../../../../services/orderService";
import { lngDetect } from "../../../../helpers/lngDetect";
import dayjs from "dayjs";
import Badge from "../../../../components/elements/Badge";
import Checkbox from "../../../../components/form/Checkbox";
import { apiRequest } from "../../../../util/apiUtils";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { classNames } from "../../../../helpers/classNames";
import Switch from "../../../../components/form/Switch";
import { t } from "i18next";

const CheckIn = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { event, eventLoading, selectedLanguage } = useOutletContext();
  const { organizations, selectedOrganization } = useOrganization();
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["checkins", filters, search, event?.data?._id],
    queryFn: async () => {
      const res = await transactionList({
        ...filters,
        ...(search && { keyword: search }),
        event: event?.data?._id,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!event?.data?._id,
  });

  const { mutate: checkInHandler, isPending: checkIsPending } = useMutation({
    mutationFn: async ({ val, checked }) => {
      const res = await apiRequest("patch", "order/ticket/checkin", {
        body: {
          // orderItemUid: val?.uid,
          checkInUid: encrypt({
            text: JSON.stringify({
              orderItemUid: val?.uid,
              ticketUid: val?.ticketInfo?.uid,
              orderUid: val?.orderData?.uid,
              eventUid: val?.orderData?.eventData?.uid,
            }),
            secretKey: "STACSEKORABRITEWE34",
          }),
          ...(!checked && { type: "check-out" }),
        },
      });
      return res?.data;
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        queryClient.setQueryData(
          ["checkins", filters, search, event?.data?._id],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = (stateData || []).map((elm) =>
                elm?._id === res?.data?._id
                  ? { ...elm, checkIn: res?.data?.checkIn }
                  : elm
              );
              return { ...state, data: stateData };
            }
          }
        );
      }
    },
  });

  const tableHeadData = [
    {
      _id: 3,
      name: t("Attendee"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Ticket Id"),
      align: "left",
    },
    {
      _id: "ticketName",
      name: t("ticket Name"),
      align: "left",
    },
    {
      _id: 4,
      name: t("Check-In date"),
      align: "left",
    },
    {
      _id: 4,
      name: t("Checked"),
      align: "left",
      ...tableFilter({
        content: (
          <div>
            {[
              { name: t("All"), value: "all" },
              { name: t("Checked"), value: true },
              { name: t("Not checked"), value: false },
            ]?.map((item, i) => (
              <button
                type="button"
                key={i?.toString()}
                className="flex items-center gap-1 w-full text-sm font-medium py-2 px-3 rounded-md cursor-pointer hover:bg-primary/10 transition-all duration-200"
                onClick={() => {
                  if (item?.value === "all") {
                    setFilters((pre) => {
                      delete pre?.checkIn;
                      return { ...pre, page: 1 };
                    });
                  } else {
                    setFilters((pre) => ({
                      ...pre,
                      checkIn: item?.value,
                      page: 1,
                    }));
                  }
                }}
              >
                <div
                  className={classNames(
                    "flex items-center justify-center w-5 h-5 rounded-md border border-slate-300",
                    (!filters.hasOwnProperty("checkIn") &&
                      item?.value === "all") ||
                      (filters.hasOwnProperty("checkIn") &&
                        filters?.checkIn === item?.value)
                      ? "border-primary"
                      : ""
                  )}
                >
                  {((!filters.hasOwnProperty("checkIn") &&
                    item?.value === "all") ||
                    (filters.hasOwnProperty("checkIn") &&
                      filters?.checkIn === item?.value)) && (
                    <i className="fa-fw fa-regular fa-check text-xs"></i>
                  )}
                </div>
                {item?.name}
              </button>
            ))}
          </div>
        ),
      }),
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    const attendee_name = item?.attendeeData
      ?.filter((attendee) =>
        ["First Name", "Last Name"].includes(attendee?.question)
      )
      ?.map((attendee) => `${attendee?.answers || ""}`.trim())
      .join(" ");
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: attendee_name || "-",
        },
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.ticketInfo?.uid || "-",
        },
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: lngDetect(item?.ticketInfo?.name) || "-",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.checkIn?.date
            ? dayjs(item?.checkIn?.date).format("MMM DD, YYYY hh:mm A")
            : "N/A",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex items-center gap-2">
              {/* <Badge
                label={item?.checkIn?.value ? "yes" : "no"}
                type={item?.checkIn?.value ? "success" : "danger"}
              />
              <div className="relative">
                <Checkbox
                  checked={!!item?.checkIn?.value}
                  onChange={(e) =>
                    checkInHandler({ val: item, checked: e.target.checked })
                  }
                />
                {checkIsPending && (
                  <div className="flex items-center justify-center absolute top-0 left-0 w-full h-full">
                    <i className="fa-duotone fa-solid fa-spinner-third animate-spin"></i>
                  </div>
                )}
              </div> */}
              <Switch
                className={classNames(
                  "before:text-[8px]",
                  item?.checkIn?.value
                    ? "before:content-['yes'] before:text-green-500"
                    : "before:content-['no'] before:text-red-500"
                )}
                checked={!!item?.checkIn?.value}
                onChange={(e) => {
                  checkInHandler({ val: item, checked: e.target.checked });
                }}
              />
            </div>
          ),
        },
      ],
    };
  });

  return (
    <div className="space-y-8">
      <div className="space-y-5">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">
              {t("Check in")}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full md:w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-full md:w-72">
              <Search
                placeholder={t("Search here")}
                divClasses={"mb-0"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData || []}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
        }}
        originalData={[...(data?.data || [])]}
        isOrderingDisabled={true}
        isFetching={isFetching}
        tableActionDropdownContainer={"!w-48"}
      />
    </div>
  );
};

export default CheckIn;
